/**
 * WCAG 2.1 relative luminance + contrast ratio.
 * Formula: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * and https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 * No external deps — inlined sRGB math.
 */

function parseHex(color: string | undefined | null): [number, number, number] | null {
  if (!color) return null;
  let c = color.trim();
  if (c.startsWith("#")) c = c.slice(1);
  if (c.length === 3) {
    c = c.split("").map((ch) => ch + ch).join("");
  }
  if (c.length !== 6) return null;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  if ([r, g, b].some((v) => isNaN(v))) return null;
  return [r, g, b];
}

function channelLuminance(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb;
  return (
    0.2126 * channelLuminance(r) +
    0.7152 * channelLuminance(g) +
    0.0722 * channelLuminance(b)
  );
}

/**
 * Returns WCAG contrast ratio (1..21) or null if colors can't be parsed.
 */
export function contrastRatio(
  fg: string | undefined | null,
  bg: string | undefined | null
): number | null {
  const fgRgb = parseHex(fg);
  const bgRgb = parseHex(bg);
  if (!fgRgb || !bgRgb) return null;
  const l1 = relativeLuminance(fgRgb);
  const l2 = relativeLuminance(bgRgb);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export type ContrastLevel = "pass" | "warn" | "fail";

export function contrastLevel(ratio: number | null): ContrastLevel {
  if (ratio === null) return "pass";
  if (ratio < 3) return "fail";
  if (ratio < 4.5) return "warn";
  return "pass";
}
